import { useEffect, useMemo, useState } from 'react';

/**
 * Looping, non-interactive marketing demo of the StoryMint flow.
 * Cycles through Setup → Generating → Reading every ~6.5s.
 * Decorative only — aria-hidden, no interactive controls.
 */

const STAGE_MS = 6500;
const URLS = [
  'app.storymint.io/books/new',
  'app.storymint.io/books/abc-1f3a',
  'app.storymint.io/books/abc-1f3a/editor',
];

const PREMISE_TEXT =
  'A failed cartographer inherits a map that redraws itself each night, leading her into the ruined city beneath her own.';

const GENERATION_STEPS = [
  { label: 'Creating outline', percent: 22 },
  { label: 'Building characters', percent: 41 },
  { label: 'Writing chapter 6 of 20', percent: 58 },
  { label: 'Writing chapter 14 of 20', percent: 78 },
  { label: 'Polishing manuscript', percent: 94 },
  { label: 'Complete', percent: 100 },
];

const CHAPTER_LIST = [
  'The Map That Knows',
  'A City Beneath',
  'The First Disappearance',
  'Inkblood',
  'Names in the Margin',
  'The Cartographer’s Daughter',
];

const READING_PARAGRAPHS = [
  'The map had changed again overnight. Where the harbor had been, there was now a forest of narrow streets, each one named after a sister Maren had never had.',
  'She traced one with her thumb — Ada’s Walk — and felt the parchment shiver, the way a sleeping animal twitches when it dreams.',
  'Below the forest, in fresh, wet ink, three words she did not remember writing: “Come find us.”',
  'It was the third such message that month. The first had been a single letter. The second, her own name in a handwriting that wasn’t hers but knew the shape of her serifs.',
  'She pressed the map flat against the table and counted the streets again. Twelve. Always twelve, no matter how the city beneath rearranged itself.',
  'Outside the workshop window, the harbor — the real harbor, the one that hadn’t moved in a hundred years — kept on existing without her, indifferent to whatever was being drawn into being.',
  'Maren rolled the map closed, tied it with the leather cord her father had left her, and reached for her coat. If the city wanted to be found, she would let it be found.',
];

function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setPrefers(mq.matches);
    handler();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return prefers;
}

function BrowserChrome({ url }: { url: string }) {
  return (
    <div
      className="h-[38px] border-b border-border flex items-center px-4 gap-1.5 shrink-0"
      style={{ background: '#16161f' }}
    >
      <span className="w-3 h-3 rounded-full bg-[#ff5f57] shrink-0" />
      <span className="w-3 h-3 rounded-full bg-[#febc2e] shrink-0" />
      <span className="w-3 h-3 rounded-full bg-[#28c840] shrink-0" />
      <div
        className="flex-1 max-w-[360px] mx-auto h-[22px] rounded flex items-center justify-center text-[11px] text-muted-foreground font-mono truncate px-3 transition-[max-width] duration-500"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      >
        {url}
      </div>
    </div>
  );
}

/* ─────────────────────────── Stage 1: Setup ─────────────────────────── */

function SetupStage({ active }: { active: boolean }) {
  const [typed, setTyped] = useState(0);

  useEffect(() => {
    if (!active) {
      setTyped(0);
      return;
    }
    let idx = 0;
    const id = setInterval(() => {
      idx += 1;
      setTyped(idx);
      if (idx >= PREMISE_TEXT.length) clearInterval(id);
    }, 32);
    return () => clearInterval(id);
  }, [active]);

  const typedText = PREMISE_TEXT.slice(0, typed);
  const isTypingDone = typed >= PREMISE_TEXT.length;

  return (
    <div className="absolute inset-0 px-6 sm:px-10 py-8 overflow-hidden">
      <div className="max-w-2xl mx-auto">
        <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-3">
          New book
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-6 leading-tight">
          Tell us about your story
        </h3>

        {/* Title field (pre-filled) */}
        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Title</label>
        <div className="w-full px-3 py-2 rounded-md bg-[#0d0d12] border border-border text-sm text-foreground mb-4">
          The Drawn City
        </div>

        {/* Premise textarea — animated typing */}
        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Premise</label>
        <div className="w-full min-h-[96px] px-3 py-2 rounded-md bg-[#0d0d12] border border-primary/40 text-sm text-foreground mb-4 leading-relaxed">
          {typedText}
          {!isTypingDone && (
            <span className="inline-block w-[1px] h-[14px] align-middle ml-0.5 bg-primary animate-pulse" />
          )}
        </div>

        {/* Genre chips */}
        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Genre</label>
        <div className="flex flex-wrap gap-2 mb-5">
          {['Romance', 'Thriller', 'Fantasy', 'Mystery', 'Sci-Fi'].map((g, i) => {
            const isSelected = isTypingDone && g === 'Fantasy';
            const isHinting = !isTypingDone && i === 2;
            return (
              <span
                key={g}
                className={`text-xs px-3 py-1 rounded-full border transition-all duration-300 ${
                  isSelected
                    ? 'border-primary bg-primary/15 text-primary font-semibold scale-105'
                    : isHinting
                      ? 'border-primary/40 text-foreground'
                      : 'border-border text-muted-foreground'
                }`}
              >
                {g}
              </span>
            );
          })}
        </div>

        {/* Length pills */}
        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Length</label>
        <div className="flex gap-2 mb-6">
          {[
            { label: 'Short · 30k', words: 30000 },
            { label: 'Standard · 50k', words: 50000 },
            { label: 'Long · 80k', words: 80000 },
          ].map((opt) => {
            const isSelected = isTypingDone && opt.words === 50000;
            return (
              <span
                key={opt.words}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors duration-300 ${
                  isSelected
                    ? 'border-primary bg-primary/15 text-primary font-semibold'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {opt.label}
              </span>
            );
          })}
        </div>

        {/* Generate button */}
        <div
          className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-500 ${
            isTypingDone
              ? 'bg-primary text-primary-foreground shadow-[0_0_30px_-6px_hsl(var(--primary))]'
              : 'bg-primary/40 text-primary-foreground/60'
          }`}
        >
          <span>{isTypingDone ? '✨ Generate my novel' : 'Generate my novel'}</span>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Stage 2: Generating ────────────────────────── */

function GeneratingStage({ active }: { active: boolean }) {
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (!active) {
      setStepIdx(0);
      return;
    }
    const stepDuration = STAGE_MS / GENERATION_STEPS.length;
    let idx = 0;
    const id = setInterval(() => {
      idx = Math.min(idx + 1, GENERATION_STEPS.length - 1);
      setStepIdx(idx);
    }, stepDuration);
    return () => clearInterval(id);
  }, [active]);

  const step = GENERATION_STEPS[stepIdx]!;
  const completedChapters = Math.floor((step.percent / 100) * CHAPTER_LIST.length);

  return (
    <div className="absolute inset-0 px-6 sm:px-10 py-8 overflow-hidden">
      <div className="max-w-3xl mx-auto">
        {/* Title row */}
        <div className="flex items-center justify-between mb-6 gap-4">
          <div className="min-w-0">
            <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-1.5">
              Generating
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground truncate">
              The Drawn City
            </h3>
          </div>
          <span
            className={`shrink-0 text-xs px-3 py-1 rounded-full font-semibold transition-colors duration-300 ${
              step.percent >= 100
                ? 'bg-green-900/40 text-green-400'
                : 'bg-blue-900/40 text-blue-400'
            }`}
          >
            {step.percent >= 100 ? 'Complete' : 'Writing…'}
          </span>
        </div>

        {/* Status text */}
        <div className="flex items-center gap-3 mb-3">
          {step.percent < 100 && (
            <span
              aria-hidden="true"
              className="inline-block w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin"
            />
          )}
          <span className="text-sm text-foreground transition-opacity duration-300">
            {step.label}
            {step.percent < 100 && <span className="text-muted-foreground">…</span>}
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-2 w-full rounded-full bg-[#1a1a2e] overflow-hidden mb-2">
          <div
            className="h-full bg-primary transition-[width] duration-700 ease-out"
            style={{ width: `${step.percent}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-6">
          <span>{step.percent}%</span>
          <span>Estimated 2 minutes remaining</span>
        </div>

        {/* Two-col: chapters + cover */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Chapter list */}
          <div className="sm:col-span-2">
            <div className="text-xs font-semibold text-muted-foreground mb-2">Chapters</div>
            <ul className="space-y-1.5">
              {CHAPTER_LIST.slice(0, 6).map((title, i) => {
                const done = i < completedChapters;
                const current = i === completedChapters && step.percent < 100;
                return (
                  <li
                    key={title}
                    className={`flex items-center gap-2.5 text-sm transition-colors duration-300 ${
                      done ? 'text-foreground' : current ? 'text-foreground' : 'text-muted-foreground/60'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold transition-colors duration-300 ${
                        done
                          ? 'bg-primary text-primary-foreground'
                          : current
                            ? 'border border-primary text-primary'
                            : 'border border-border text-muted-foreground/40'
                      }`}
                    >
                      {done ? '✓' : i + 1}
                    </span>
                    <span className="truncate">{title}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Cover thumb */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-2">Cover</div>
            <div
              className="aspect-[2/3] rounded-md border border-border overflow-hidden flex flex-col items-center justify-center text-center p-3"
              style={{
                background:
                  'linear-gradient(160deg, #2a1850 0%, #1a1a2e 55%, #0d0d12 100%)',
              }}
            >
              <span className="text-2xl mb-2" aria-hidden="true">🗺️</span>
              <div className="text-[10px] font-bold text-primary uppercase tracking-wider mb-1">
                A Novel
              </div>
              <div className="text-xs font-bold text-foreground leading-tight">
                The Drawn City
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Stage 3: Reading ────────────────────────── */

function ReadingStage({ active, reducedMotion }: { active: boolean; reducedMotion: boolean }) {
  const [paragraphsVisible, setParagraphsVisible] = useState(reducedMotion ? READING_PARAGRAPHS.length : 0);

  useEffect(() => {
    if (!active || reducedMotion) {
      setParagraphsVisible(reducedMotion ? READING_PARAGRAPHS.length : 0);
      return;
    }
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      setParagraphsVisible(count);
      if (count >= READING_PARAGRAPHS.length) clearInterval(id);
    }, 650);
    return () => clearInterval(id);
  }, [active, reducedMotion]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="grid grid-cols-[180px_1fr] sm:grid-cols-[220px_1fr] h-full">
        {/* Sidebar — chapter list */}
        <aside
          className="border-r border-border overflow-hidden p-4 hidden sm:block"
          style={{ background: '#0d0d12' }}
        >
          <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">
            The Drawn City
          </div>
          <ul className="space-y-1.5">
            {CHAPTER_LIST.map((title, i) => {
              const isSelected = i === 0;
              return (
                <li
                  key={title}
                  className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md transition-colors ${
                    isSelected
                      ? 'bg-primary/15 text-primary font-semibold'
                      : 'text-muted-foreground'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold shrink-0"
                  >
                    ✓
                  </span>
                  <span className="truncate">{title}</span>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Reader pane */}
        <div className="overflow-hidden p-6 sm:p-10">
          <div className="max-w-prose mx-auto">
            <div className="text-[10px] font-bold text-primary tracking-[0.12em] uppercase mb-2">
              Chapter 1
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-5 leading-tight">
              The Map That Knows
            </h3>
            <div className="space-y-3.5 text-[13px] sm:text-[14px] leading-[1.75] text-muted-foreground">
              {READING_PARAGRAPHS.map((p, i) => (
                <p
                  key={i}
                  className={`transition-all duration-700 ${
                    i < paragraphsVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-1'
                  }`}
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Wrapper / loop ─────────────────────────── */

export function HowItWorksDemo() {
  const reducedMotion = usePrefersReducedMotion();
  const [stage, setStage] = useState(reducedMotion ? 2 : 0);

  useEffect(() => {
    if (reducedMotion) {
      setStage(2);
      return;
    }
    const id = setInterval(() => {
      setStage((s) => (s + 1) % 3);
    }, STAGE_MS);
    return () => clearInterval(id);
  }, [reducedMotion]);

  const stageLabel = useMemo(
    () => ['Set up your story', 'Generate your novel', 'Read & publish'][stage],
    [stage],
  );

  return (
    <div
      role="img"
      aria-label={`StoryMint demo: ${stageLabel}`}
      className="rounded-xl overflow-hidden border border-border mb-12"
      style={{ background: '#111118', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}
    >
      <BrowserChrome url={URLS[stage] ?? URLS[0]!} />

      <div
        className="relative w-full"
        style={{
          height: 'clamp(560px, 64vw, 640px)',
          background: 'linear-gradient(135deg, #0d0d12 0%, #16162a 100%)',
        }}
        aria-hidden="true"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              stage === i ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            {i === 0 && <SetupStage active={stage === 0} />}
            {i === 1 && <GeneratingStage active={stage === 1} />}
            {i === 2 && <ReadingStage active={stage === 2} reducedMotion={reducedMotion} />}
          </div>
        ))}

        {/* Stage indicator dots */}
        <div
          aria-hidden="true"
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                stage === i ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
